import { VideoShareUserPost } from "api/interfaces/videoshareInterface.interface";
import { createUserVideoShare, updateVideoSharing } from "api/videoshareAPI";
import { forwardRef, useCallback, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import AddVSPresenter from "./AddVSPresenter";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { checkEmailRegEx } from "utils/regEx";
import { getConvertedUTCDate } from "utils/timeUtil";
import useApiError from "hook/useApiError";
import {
  CustomError,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import { useSearchParams } from "react-router-dom";
import { checkEmpty } from "utils/functions";
import { Option } from "utils/options";
type Props = {
  small?: boolean;
  index: number;
  videoId: string;
  isShare: boolean;
  onChangeModal: (type: string, value: boolean) => void;
};

export const getDefaultExpires = (timezone: string) => {
  const expires = new Date();
  expires.setDate(expires.getDate() + 7);
  return getConvertedUTCDate(expires, timezone);
};

const getInitialState = (searchParams: URLSearchParams, timezone: string) => {
  const defaultState = {
    user: [],
    message: "",
    expires: getDefaultExpires(timezone),
    canDownload: false,
  };
  const shareEmail = searchParams.get("share");
  const shareLabel = searchParams.get("sharelabel");
  if (!!shareEmail && !!shareLabel) {
    return {
      ...defaultState,
      user: [{
        email: shareEmail,
        name: shareLabel,
      }],
    };
  }
  return defaultState;
};

const AddVSContainer = forwardRef<HTMLDivElement, Props>(
  (
    { small, index, videoId, isShare, onChangeModal }: Props,
    ref
  ): JSX.Element => {
    const { handleErrorNotify, notifySuccess, lang, intlString } =
      useApiError();

    const selectedAccount: ProfileAccountInfo = useAppSelector(
      (state) => state.accountSettings
    );

    const [isExpired, setIsExpired] = useState<boolean>(true);
    const [searchParams] = useSearchParams();
    const [state, setState] = useState<VideoShareUserPost>(
      getInitialState(searchParams, selectedAccount.timezone)
    );
    const [validateMsg, setValidateMsg] = useState<ValidateMsg>({
      emptyUser: "",
      invalidEmail: "",
    });

    const queryClient = useQueryClient();

    const today = new Date();
    //const validateShareDate = nextDay(today, 7);

    const mutationCreateUser = useMutation(createUserVideoShare, {
      onSuccess: () => {
        notifySuccess(lang.label.videoshare.notify.share.success);
        queryClient.invalidateQueries("videoshareViewUserList");
        onChangeModal("add", false);
      },
      onError: (err: CustomError) => {
        handleErrorNotify(err, lang.label.videoshare.notify.share.fail);
      },
    });

    const mutationUpdateSharing = useMutation(updateVideoSharing, {
      onSuccess: () => {
        notifySuccess(lang.label.videoshare.notify.share.update.success);
        queryClient.invalidateQueries("videoShare");
      },
      onError: (err: CustomError) => {
        handleErrorNotify(err, lang.label.videoshare.notify.share.update.fail);
      },
    });

    const onChangeSharing = (checked: boolean) => {
      mutationUpdateSharing.mutate({
        videoId: videoId,
        sharing: checked,
      });
    };

    const onSelectUser = (user: Option[]) => {
      setState({
        ...state,
        user: user.map((option) => ({
          email: option.value,
          name: option.label,
        })),
      });
    };

    useEffect(() => {}, [isShare]);
    const onChangeValue = (e: any) => {
      let {
        target: { checked, name, value },
      } = e;

      if (name === "isExpired") {
        setIsExpired(checked);
        return;
      }
      if (name === "canDownload") {
        setState({
          ...state,
          canDownload: checked,
        });
        return;
      }

      setState({
        ...state,
        [name]: value,
      });
    };

    const onChangeDateValue = (date: string) => {
      setState({
        ...state,
        expires: date,
      });
    };

    const onAddUser = useCallback(() => {
      // console.log(state);
      if (state.user.length === 0) {
        setValidateMsg({
          emptyUser: intlString(lang.validateMsg.videoShare.err.emptyUser),
          invalidEmail: "",
        });
        return;
      }

      //BUG [RND-380]
      const isAllValid = state.user.every(
        ({email}) => !checkEmpty(email) && checkEmailRegEx(email)
      );
      if (!isAllValid) {
        setValidateMsg({
          emptyUser: "",
          invalidEmail: intlString(
            lang.validateMsg.videoShare.err.invalidEmail
          ),
        });
        return;
      }

      mutationCreateUser.mutate({
        videoId: videoId,
        accountId: selectedAccount.accountId,
        payload: {
          ...state,
          expires: isExpired ? state.expires : null,
        },
      });
    }, [
      isExpired,
      mutationCreateUser,
      selectedAccount.accountId,
      selectedAccount.timezone,
      state,
      videoId,
    ]);

    return (
      <AddVSPresenter
        ref={ref}
        small={small}
        index={index}
        onChangeModal={onChangeModal}
        onChangeValue={onChangeValue}
        onChangeDateValue={onChangeDateValue}
        state={state}
        isExpired={isExpired}
        onAddUser={onAddUser}
        onSelectUser={onSelectUser}
        today={today}
        //validateShareDate={validateShareDate}
        validateMsg={validateMsg}
        isShare={isShare}
        onChangeSharing={onChangeSharing}
      />
    );
  }
);

export default AddVSContainer;
